<template>
    <div class="fb-canvas">
        <!-- Empty state -->
        <div v-if="steps.length === 0" class="fb-canvas-empty">
            <div class="fb-canvas-empty-illustration">
                <div class="fb-canvas-empty-circle">
                    <i class="fal fa-layer-group"></i>
                </div>
                <div class="fb-canvas-empty-dots">
                    <span></span><span></span><span></span>
                </div>
            </div>
            <h3 class="fb-canvas-empty-title">{{ $t('form_builder.canvas.empty_title') }}</h3>
            <p class="fb-canvas-empty-desc">{{ $t('form_builder.canvas.empty_desc') }}</p>
            <button class="fb-canvas-empty-btn" @click="addStep">
                <i class="fal fa-plus"></i>
                {{ $t('form_builder.canvas.add_step') }}
            </button>
        </div>

        <div v-else class="fb-canvas-inner">
            <draggable
                :list="steps"
                handle=".fb-step-handle"
                animation="200"
                class="fb-steps"
                @end="reorderSteps"
            >
                <div
                    v-for="(step, stepIndex) in steps"
                    :key="step._uid || step.id"
                    class="fb-step"
                    :class="{ 'fb-step-active': activeStepIndex === stepIndex }"
                >
                    <!-- Step header -->
                    <div class="fb-step-header" @click="selectStep(stepIndex)">
                        <div class="d-flex align-items-center">
                            <i class="fal fa-grip-vertical fb-step-handle mr-2"></i>
                            <span class="fb-step-number">{{ stepIndex + 1 }}</span>
                            <div class="fb-step-info">
                                <input
                                    class="fb-step-name"
                                    v-model="step.name"
                                    :placeholder="$t('form_builder.canvas.step_name')"
                                    @click.stop
                                />
                                <input
                                    class="fb-step-desc"
                                    v-model="step.content"
                                    :placeholder="$t('form_builder.canvas.step_description')"
                                    @click.stop
                                />
                            </div>
                        </div>
                        <div class="fb-step-actions">
                            <span class="fb-step-count">
                                {{ step.attributes.length }} {{ $t('form_builder.canvas.fields') }}
                            </span>
                            <button
                                class="fb-step-delete"
                                @click.stop="removeStep(stepIndex)"
                                :title="$t('common.delete')"
                            >
                                <i class="fal fa-trash-alt"></i>
                            </button>
                        </div>
                    </div>

                    <!-- Step fields (drop zone) -->
                    <draggable
                        :list="step.attributes"
                        :group="{ name: 'fields', pull: true, put: true }"
                        animation="200"
                        class="fb-step-fields"
                        :class="{ 'fb-step-fields-empty': step.attributes.length === 0 }"
                        @end="reorderFields(stepIndex)"
                    >
                        <div
                            v-for="(field, fieldIndex) in step.attributes"
                            :key="field._uid || field.id"
                            class="fb-field"
                            :class="{ 'fb-field-active': isFieldSelected(stepIndex, fieldIndex) }"
                            @click.stop="selectField(stepIndex, fieldIndex)"
                        >
                            <i class="fal fa-grip-vertical fb-field-drag"></i>
                            <div class="fb-field-type-icon">
                                <i :class="getFieldIcon(field)"></i>
                            </div>
                            <div class="fb-field-info">
                                <span class="fb-field-label">
                                    {{ getFieldLabel(field) || $t('form_builder.canvas.untitled_field') }}
                                </span>
                                <span class="fb-field-type">{{ getFieldTypeName(field) }}</span>
                            </div>
                            <div class="fb-field-badges">
                                <span v-if="field.requiredPublic" class="fb-badge fb-badge-required">R</span>
                                <span v-if="field.hidden" class="fb-badge fb-badge-hidden">
                                    <i class="fal fa-eye-slash"></i>
                                </span>
                            </div>
                            <button
                                class="fb-field-delete"
                                @click.stop="removeField(stepIndex, fieldIndex)"
                            >
                                <i class="fal fa-times"></i>
                            </button>
                        </div>
                    </draggable>

                    <!-- Empty state -->
                    <div v-if="step.attributes.length === 0" class="fb-step-empty">
                        <i class="fal fa-arrow-down mr-1"></i>
                        {{ $t('form_builder.canvas.drop_here') }}
                    </div>
                </div>
            </draggable>

            <!-- Add step button -->
            <button class="fb-add-step" @click="addStep">
                <i class="fal fa-plus mr-2"></i>
                {{ $t('form_builder.canvas.add_step') }}
            </button>
        </div>
    </div>
</template>

<script>
import draggable from 'vuedraggable'

const FIELD_ICONS = {
    1: 'fal fa-font',
    2: 'fal fa-eye-slash',
    3: 'fal fa-align-left',
    4: 'fal fa-paragraph',
    5: 'fal fa-hashtag',
    6: 'fal fa-map-marker-alt',
    7: 'fal fa-calendar',
    8: 'fal fa-calendar-alt',
    9: 'fal fa-list-alt',
    10: 'fal fa-dot-circle',
    11: 'fal fa-check-square',
    12: 'fal fa-cloud-upload',
    13: 'fal fa-folder-tree',
    14: 'fal fa-shield-alt',
    15: 'fal fa-database',
    16: 'fal fa-envelope',
    17: 'fal fa-star',
    18: 'fal fa-microphone'
}

const FIELD_TYPE_NAMES = {
    1: 'Texte court', 2: 'Caché', 3: 'Texte long', 4: 'Texte riche',
    5: 'Nombre', 6: 'Localisation', 7: 'Date', 8: 'Date & heure',
    9: 'Liste déroulante', 10: 'Radio', 11: 'Checkbox', 12: 'Fichier',
    13: 'Catégorie', 14: 'RGPD', 15: 'Requête', 16: 'Email', 17: 'Notation',
    18: 'Prise audio'
}

const FILE_TYPE_ICONS = {
    pictures: 'fal fa-image',
    audio: 'fal fa-headphones',
    videos: 'fal fa-video',
    files: 'fal fa-file-pdf'
}

const FILE_TYPE_NAMES = {
    pictures: 'Images',
    audio: 'Audio',
    videos: 'Vidéos',
    files: 'Documents'
}

let stepUid = 0

export default {
    name: 'FormBuilderCanvas',
    components: { draggable },
    props: {
        steps: {
            type: Array,
            required: true
        }
    },
    data() {
        return {
            activeStepIndex: null,
            activeFieldIndex: null
        }
    },
    methods: {
        getFieldIcon(field) {
            const typeId = field.form_attribute_type_id || field.typeId
            if (typeId === 12 && field.config && field.config.type) {
                return FILE_TYPE_ICONS[field.config.type] || FIELD_ICONS[12]
            }
            return FIELD_ICONS[typeId] || 'fal fa-question'
        },
        getFieldTypeName(field) {
            const typeId = field.form_attribute_type_id || field.typeId
            if (typeId === 12 && field.config && field.config.type) {
                return FILE_TYPE_NAMES[field.config.type] || FIELD_TYPE_NAMES[12]
            }
            return FIELD_TYPE_NAMES[typeId] || 'Inconnu'
        },
        getFieldLabel(field) {
            if (field.label) return field.label
            if (field.translations) {
                const fr = field.translations.fr || field.translations.find && field.translations.find((t) => t.locale === 'fr')
                if (fr) return fr.label
            }
            return ''
        },
        isFieldSelected(stepIndex, fieldIndex) {
            return this.activeStepIndex === stepIndex && this.activeFieldIndex === fieldIndex
        },
        selectStep(stepIndex) {
            this.activeStepIndex = stepIndex
            this.activeFieldIndex = null
            this.$emit('select-step', stepIndex)
        },
        selectField(stepIndex, fieldIndex) {
            this.activeStepIndex = stepIndex
            this.activeFieldIndex = fieldIndex
            this.$emit('select-field', { stepIndex, fieldIndex, field: this.steps[stepIndex].attributes[fieldIndex] })
        },
        addStep() {
            stepUid++
            const newStep = {
                _uid: `step-${stepUid}`,
                name: '',
                content: '',
                order: this.steps.length,
                attributes: [],
                translations: []
            }
            this.steps.push(newStep)
            this.selectStep(this.steps.length - 1)
        },
        removeStep(stepIndex) {
            this.steps.splice(stepIndex, 1)
            this.activeStepIndex = null
            this.activeFieldIndex = null
            this.$emit('select-field', null)
        },
        removeField(stepIndex, fieldIndex) {
            this.steps[stepIndex].attributes.splice(fieldIndex, 1)
            if (this.isFieldSelected(stepIndex, fieldIndex)) {
                this.activeFieldIndex = null
                this.$emit('select-field', null)
            }
        },
        reorderSteps() {
            this.steps.forEach((step, i) => { step.order = i })
        },
        reorderFields(stepIndex) {
            this.steps[stepIndex].attributes.forEach((attr, i) => { attr.order = i })
        }
    }
}
</script>

<style scoped>
.fb-canvas {
    flex: 1;
    overflow-y: auto;
    background: #f8fafc;
    padding: 28px;
}

/* -- Empty state -- */
.fb-canvas-empty {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    height: 100%;
    text-align: center;
    padding: 48px 24px;
}

.fb-canvas-empty-illustration {
    display: flex;
    flex-direction: column;
    align-items: center;
    margin-bottom: 24px;
}

.fb-canvas-empty-circle {
    width: 88px;
    height: 88px;
    border-radius: 50%;
    background: linear-gradient(135deg, #dbeafe, #eff6ff);
    display: flex;
    align-items: center;
    justify-content: center;
    margin-bottom: 16px;
    box-shadow: 0 4px 20px rgba(59, 130, 246, 0.1);
}

.fb-canvas-empty-circle i {
    font-size: 2.2rem;
    color: #3b82f6;
}

.fb-canvas-empty-dots {
    display: flex;
    gap: 6px;
}

.fb-canvas-empty-dots span {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: #cbd5e1;
    animation: fb-dot-pulse 1.4s ease-in-out infinite;
}

.fb-canvas-empty-dots span:nth-child(2) { animation-delay: 0.2s; }
.fb-canvas-empty-dots span:nth-child(3) { animation-delay: 0.4s; }

@keyframes fb-dot-pulse {
    0%, 80%, 100% { opacity: 0.3; transform: scale(0.8); }
    40% { opacity: 1; transform: scale(1); }
}

.fb-canvas-empty-title {
    font-size: 1.1rem;
    font-weight: 700;
    color: #0f172a;
    margin: 0 0 8px;
}

.fb-canvas-empty-desc {
    font-size: 0.85rem;
    color: #64748b;
    margin: 0 0 28px;
    max-width: 340px;
    line-height: 1.5;
}

.fb-canvas-empty-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    padding: 12px 28px;
    background: var(--theme-primary, #3b82f6);
    color: #fff;
    border: none;
    border-radius: 10px;
    font-size: 0.88rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.fb-canvas-empty-btn i { color: #fff; }

.fb-canvas-empty-btn:hover {
    box-shadow: 0 4px 14px rgba(59, 130, 246, 0.35);
    transform: translateY(-1px);
}

.fb-canvas-empty-btn:active { transform: scale(0.97); }

/* -- Inner -- */
.fb-canvas-inner {
    max-width: 720px;
    margin: 0 auto;
}

.fb-steps {
    display: flex;
    flex-direction: column;
    gap: 18px;
}

/* -- Step card -- */
.fb-step {
    background: #fff;
    border: 2px solid #e5e7eb;
    border-radius: 14px;
    overflow: hidden;
    transition: all 0.2s ease;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.03);
}

.fb-step:hover {
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
}

.fb-step-active {
    border-color: var(--theme-primary, #3b82f6);
    box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.08), 0 2px 8px rgba(0, 0, 0, 0.04);
}

.fb-step-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 14px 18px;
    background: #fafbfc;
    border-bottom: 1px solid #f1f5f9;
    cursor: pointer;
    transition: background 0.15s ease;
}

.fb-step-header:hover {
    background: #f5f7fa;
}

.fb-step-handle {
    color: #cbd5e1;
    cursor: grab;
    transition: color 0.15s;
}

.fb-step-handle:hover {
    color: #94a3b8;
}

.fb-step-number {
    width: 30px;
    height: 30px;
    border-radius: 9px;
    background: var(--theme-primary, #3b82f6);
    color: #fff;
    font-size: 0.82rem;
    font-weight: 700;
    display: flex;
    align-items: center;
    justify-content: center;
    margin-right: 14px;
    flex-shrink: 0;
}

.fb-step-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
    flex: 1;
}

.fb-step-name {
    border: none;
    background: transparent;
    font-size: 0.9rem;
    font-weight: 600;
    color: #0f172a;
    outline: none;
    padding: 0;
    width: 100%;
}

.fb-step-name::placeholder { color: #cbd5e1; }

.fb-step-desc {
    border: none;
    background: transparent;
    font-size: 0.78rem;
    color: #64748b;
    outline: none;
    padding: 0;
    width: 100%;
}

.fb-step-desc::placeholder { color: #e2e8f0; }

.fb-step-actions {
    display: flex;
    align-items: center;
    gap: 14px;
    flex-shrink: 0;
}

.fb-step-count {
    font-size: 0.72rem;
    color: #94a3b8;
    white-space: nowrap;
    background: #f1f5f9;
    padding: 3px 10px;
    border-radius: 20px;
}

.fb-step-delete {
    border: none;
    background: none;
    color: #e2e8f0;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    transition: all 0.15s;
}

.fb-step-delete:hover {
    color: #ef4444;
    background: #fef2f2;
}

/* -- Fields -- */
.fb-step-fields {
    min-height: 8px;
    padding: 10px 12px;
}

.fb-step-fields-empty {
    min-height: 60px;
}

.fb-step-empty {
    text-align: center;
    padding: 20px 16px;
    color: #94a3b8;
    font-size: 0.82rem;
    border: 2px dashed #e5e7eb;
    border-radius: 10px;
    margin: 4px;
    transition: all 0.2s ease;
}

.fb-field {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 14px;
    margin: 5px 0;
    border: 1.5px solid #f1f5f9;
    border-radius: 10px;
    background: #fff;
    cursor: pointer;
    transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
}

.fb-field:hover {
    border-color: #bfdbfe;
    background: #fafbff;
    box-shadow: 0 1px 4px rgba(59, 130, 246, 0.06);
}

.fb-field-active {
    border-color: var(--theme-primary, #3b82f6);
    background: #eff6ff;
    box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.08);
}

.fb-field-drag {
    color: #e2e8f0;
    cursor: grab;
    font-size: 0.78rem;
    transition: color 0.15s;
}

.fb-field:hover .fb-field-drag {
    color: #94a3b8;
}

.fb-field-type-icon {
    width: 34px;
    height: 34px;
    border-radius: 9px;
    background: #f1f5f9;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
    transition: background 0.15s ease;
}

.fb-field-active .fb-field-type-icon {
    background: #dbeafe;
}

.fb-field-type-icon i {
    color: var(--theme-primary, #3b82f6);
    font-size: 0.85rem;
}

.fb-field-info {
    flex: 1;
    min-width: 0;
}

.fb-field-label {
    display: block;
    font-size: 0.85rem;
    font-weight: 500;
    color: #0f172a;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.fb-field-type {
    display: block;
    font-size: 0.72rem;
    color: #94a3b8;
    margin-top: 1px;
}

.fb-field-badges {
    display: flex;
    gap: 4px;
    flex-shrink: 0;
}

.fb-badge {
    font-size: 0.62rem;
    font-weight: 700;
    padding: 2px 7px;
    border-radius: 5px;
    letter-spacing: 0.02em;
}

.fb-badge-required {
    background: #fef3c7;
    color: #d97706;
}

.fb-badge-hidden {
    background: #f1f5f9;
    color: #64748b;
}

.fb-field-delete {
    border: none;
    background: none;
    color: #e2e8f0;
    cursor: pointer;
    padding: 4px;
    border-radius: 6px;
    opacity: 0;
    transition: all 0.15s;
}

.fb-field:hover .fb-field-delete {
    opacity: 1;
}

.fb-field-delete:hover {
    color: #ef4444;
    background: #fef2f2;
}

/* -- Add step -- */
.fb-add-step {
    width: 100%;
    padding: 16px;
    margin-top: 18px;
    border: 2px dashed #d1d5db;
    border-radius: 14px;
    background: transparent;
    color: #64748b;
    font-size: 0.85rem;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s ease;
}

.fb-add-step:hover {
    border-color: var(--theme-primary, #3b82f6);
    color: var(--theme-primary, #3b82f6);
    background: #eff6ff;
    transform: translateY(-1px);
}
</style>
